import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import './AdminSettings.css';

const AdminSettings: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not admin
    if (userProfile && userProfile.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchUsers();
  }, [userProfile, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date if needed
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        };
      }) as User[];
      
      // Sort by creation date (newest first)
      usersData.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      
      setUsers(usersData);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(`Failed to load users: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, isAdmin: boolean) => {
    try {
      setSaving(userId);
      setError(null);
      
      const updateData: any = {};
      if (isAdmin) {
        updateData.role = 'admin';
      } else {
        // Remove role field for non-admins
        updateData.role = null;
      }
      
      await updateDoc(doc(db, 'userProfiles', userId), updateData);

      // Update local state
      setUsers(users.map(user => 
        user.uid === userId ? { ...user, role: isAdmin ? 'admin' : undefined } : user
      ));

      // Refresh if updating own role
      if (userId === userProfile?.uid) {
        window.location.reload();
      }
    } catch (err: any) {
      console.error('Error updating user role:', err);
      setError(`Failed to update user role: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(null);
    }
  };

  if (!userProfile || userProfile.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <div className="admin-settings">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <div className="admin-header">
        <h1>Admin Settings</h1>
        <div className="admin-subtitle-container">
          <p className="admin-subtitle">Manage user roles and permissions</p>
          <div 
            className="info-icon-wrapper"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <svg className="info-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            {showTooltip && (
              <div className="role-tooltip">
                <div className="tooltip-section">
                  <strong>👤 Agent:</strong> Can create listings, receive offers, submit offers on behalf of buyers. An agent can act as both listing agent and buyer's agent depending on the transaction.
                </div>
                <div className="tooltip-section">
                  <strong>⚙️ Admin:</strong> Full access to all agent features plus user management, pending lender approvals, and system settings.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>User Type</th>
              <th>NMLS</th>
              <th>Role</th>
              <th>Verification</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.uid}>
                <td className="user-name">
                  {user.firstName} {user.lastName}
                </td>
                <td className="user-email">{user.email}</td>
                <td>
                  <span className="badge badge-type">
                    {user.userType === 'loan_originator' ? 'Loan Originator' : 'Real Estate Agent'}
                  </span>
                </td>
                <td className="user-nmls">{user.nmls || '—'}</td>
                <td>
                  <select
                    value={user.role === 'admin' ? 'admin' : 'agent'}
                    onChange={(e) => updateUserRole(user.uid, e.target.value === 'admin')}
                    disabled={saving === user.uid}
                    className="role-select"
                  >
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                  {saving === user.uid && <span className="saving-indicator">Saving...</span>}
                </td>
                <td>
                  <span className={`badge badge-${user.verificationStatus || 'pending'}`}>
                    {user.verificationStatus || 'pending'}
                  </span>
                </td>
                <td className="user-date">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSettings;
